import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Grid2,
  Typography,
} from '@mui/material';
import { ReactComponent as EditIcon } from '../../../../assets/SVG/edit icon.svg';
import { useApprovalRuleDetail } from '../inventoryApproval.queries';
import { useRoles } from '../CreateApprovalRule/createApprovalRule.queries';
import useCurrency from '../../../../components/CurrencyProvider';
import { useTranslation } from 'react-i18next';
import {
  viewRuleContainerSx,
  viewRuleHeaderSx,
  viewRuleTitleSx,
  editBtnSx,
  viewSectionBoxSx,
  viewSectionTitleSx,
  detailLabelSx,
  detailValueSx,
  levelCardSx,
  viewFooterSx,
  backToListBtnSx,
} from './ViewApprovalRuleSx';

const DetailItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box>
    <Typography sx={detailLabelSx}>{label}</Typography>
    <Typography sx={detailValueSx}>{value ?? '—'}</Typography>
  </Box>
);

const ViewApprovalRule: React.FC = () => {
  const navigate = useNavigate();
  const { ruleId } = useParams<{ ruleId: string }>();
  const { currencySymbol, formatNumber } = useCurrency();
  const { t: tInventoryApproval } = useTranslation('inventory_approval_config');

  const { data: rule, isLoading } = useApprovalRuleDetail(
    ruleId ? Number(ruleId) : undefined,
  );
  const { data: rolesData } = useRoles();

  const roleMap = React.useMemo(() => {
    const map: Record<number, string> = {};
    rolesData?.objects.forEach((r) => {
      map[r.id] = r.name_label;
    });
    return map;
  }, [rolesData]);

  if (isLoading || !rule) {
    return (
      <Box sx={viewRuleContainerSx} display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  const formatEscalationTime = (hours: number | null, minutes: number | null) => {
    if (hours == null) return '—';
    const h = String(hours).padStart(2, '0');
    const m = String(minutes ?? 0).padStart(2, '0');
    return `${h}:${m} ${tInventoryApproval('hours')}`;
  };


  return (
    <Box sx={viewRuleContainerSx}>
      {/* Header */}
      <Box sx={viewRuleHeaderSx}>
        <Typography sx={viewRuleTitleSx}>
          {tInventoryApproval('view_approval_rule')}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          sx={editBtnSx}
          onClick={() => navigate(`/ai/configuration/inventory-approval/${ruleId}/edit`)}
          data-testid="view-approval-edit-btn"
        >
          {tInventoryApproval('edit')}
        </Button>
      </Box>

      {/* ── Approval Rule Details ── */}
      <Box sx={viewSectionBoxSx}>
        <Typography sx={viewSectionTitleSx}>
          {tInventoryApproval('approval_rule_details')}
        </Typography>
        <Grid2 container spacing={3}>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <DetailItem label={tInventoryApproval('rule_name')} value={rule.name} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <DetailItem label={tInventoryApproval('clinic')} value={rule.clinic_name} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <DetailItem label={tInventoryApproval('document_type')} value={rule.document_type_name} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <DetailItem label={tInventoryApproval('status')} value={rule.status === 'ACTIVE' ? tInventoryApproval('active') : tInventoryApproval('inactive')} />
          </Grid2>
        </Grid2>

        <Grid2 container spacing={3} mt={1}>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <DetailItem label={tInventoryApproval('min_amount', { symbol: currencySymbol })} value={`${currencySymbol}${formatNumber(Number(rule.min_amount))}`} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <DetailItem
              label={tInventoryApproval('max_amount', { symbol: currencySymbol })}
              value={rule.max_amount !== null ? `${currencySymbol}${formatNumber(Number(rule.max_amount))}` : tInventoryApproval('unlimited')}
            />
          </Grid2>
        </Grid2>
      </Box>

      {/* ── Authorization Levels ── */}
      <Box sx={viewSectionBoxSx}>
        <Typography sx={viewSectionTitleSx}>
          {tInventoryApproval('authorization_levels')}
        </Typography>
        {rule.levels.map((lvl) => (
          <Box key={lvl.id} sx={levelCardSx}>
            <Grid2 container spacing={3}>
              <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                <DetailItem label={tInventoryApproval('level_label')} value={tInventoryApproval('level_value', { level: lvl.level_number })} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 2.5 }}>
                <DetailItem label={tInventoryApproval('role')} value={roleMap[lvl.role] ?? lvl.role_name} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 2.5 }}>
                <DetailItem label={tInventoryApproval('escalation_time')} value={formatEscalationTime(lvl.escalation_time_hours, lvl.escalation_time_minutes)} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 2.5 }}>
                <DetailItem label={tInventoryApproval('escalation_role')} value={lvl.escalation_role ? (roleMap[lvl.escalation_role] ?? '—') : '—'} />
              </Grid2>
              <Grid2 size={{ xs: 6, sm: 3, md: 1.25 }}>
                <DetailItem label={tInventoryApproval('mandatory_toggle')} value={lvl.is_mandatory ? tInventoryApproval('on') : tInventoryApproval('off')} />
              </Grid2>
              <Grid2 size={{ xs: 6, sm: 3, md: 1.25 }}>
                <DetailItem label={tInventoryApproval('skip_if_same_user')} value={lvl.skip_if_same_user ? tInventoryApproval('on') : tInventoryApproval('off')} />
              </Grid2>
            </Grid2>
          </Box>
        ))}
      </Box>

      {/* ── Notification Settings ── */}
      <Box sx={viewSectionBoxSx}>
        <Typography sx={viewSectionTitleSx}>
          {tInventoryApproval('notification_settings')}
        </Typography>
        <Grid2 container spacing={3}>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <DetailItem
              label={tInventoryApproval('notification_channels')}
              value={
                [
                  rule.is_system && tInventoryApproval('notify_system'),
                  rule.is_email && tInventoryApproval('notify_email'),
                ].filter(Boolean).join('  |  ') || '—'
              }
            />
          </Grid2>
        </Grid2>
      </Box>

      {/* ── Footer ── */}
      <Box sx={viewFooterSx}>
        <Button
          variant="contained"
          sx={backToListBtnSx}
          onClick={() => navigate('/ai/configuration/inventory-approval')}
          data-testid="view-approval-back-btn"
        >
          {tInventoryApproval('back_to_list')}
        </Button>
      </Box>
    </Box>
  );
};

export default ViewApprovalRule;
